import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { fetchCategories } from '@/store/slices/productsSlice';
import { fetchAllFilterOptions } from '@/store/slices/filterOptionsSlice';
import { categoriesApi, productsApi } from '@/services/api';
import { PRODUCT_CONDITIONS } from '@/utils/constants';
import { 
  setFilters,
  toggleCategory, 
  toggleProductType, 
  toggleBrand, 
  toggleCondition, 
  toggleLocation, 
  setPriceRange, 
  setSearchQuery, 
  clearAllFilters,
  filterProducts 
} from '@/store/slices/productFiltersSlice';
import {
  FunnelIcon,
  TagIcon,
  CurrencyDollarIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ProductSidebarProps {
  onFilterChange?: (key: string, value: any) => void;
  currentFilters?: any;
}

const ProductSidebar: React.FC<ProductSidebarProps> = ({ onFilterChange, currentFilters }) => {
  const dispatch = useDispatch();
  const { categories: reduxCategories } = useSelector((state: RootState) => state.products);

  // Local state for all categories (including subcategories)
  const [allCategories, setCategories] = useState<any[]>([]);
  const { 
    brands, 
    conditions, 
    productTypes, 
    locations, 
    priceRange: dynamicPriceRange,
    isLoading: isFilterOptionsLoading 
  } = useSelector((state: RootState) => state.filterOptions);
  
  const {
    category_ids,
    product_types,
    brands: selectedBrands,
    conditions: selectedConditions,
    locations: selectedLocations,
    min_price,
    max_price,
    search_query,
    isLoading: isFiltering
  } = useSelector((state: RootState) => state.productFilters);
  
  // Debug Redux state changes
  useEffect(() => {
    console.log('Redux state changed:', { category_ids, product_types, selectedBrands, selectedConditions, selectedLocations });
  }, [category_ids, product_types, selectedBrands, selectedConditions, selectedLocations]);
  
  const [expandedItems, setExpandedItems] = useState<string[]>(['brands', 'conditions', 'priceRange']);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const isApplyingFilters = useRef(false);
  
  // Local state to prevent checkbox blinking
  const [localCategoryIds, setLocalCategoryIds] = useState<number[]>([]);
  const [localProductTypes, setLocalProductTypes] = useState<string[]>([]);
  const [localBrands, setLocalBrands] = useState<string[]>([]);
  const [localConditions, setLocalConditions] = useState<string[]>([]);
  const [localLocations, setLocalLocations] = useState<string[]>([]);
  
  // Track if we've manually updated local state
  const hasManuallyUpdated = useRef(false);
  const lastApiCallTime = useRef(0);

  // Initialize sidebar
  useEffect(() => {
    const initializeSidebar = async () => {
      try {
        // Fetch filter options (including categories with counts)
        await dispatch(fetchAllFilterOptions() as any);

        // Always try to get categories - first with counts, then fallback
        let categoriesData: any[] = [];

        // Try to get categories with counts first
        try {
          console.log('Trying to fetch categories with counts...');
          const categoriesWithCountsResponse = await productsApi.getCategories();
          if (categoriesWithCountsResponse?.data && Array.isArray(categoriesWithCountsResponse.data)) {
            categoriesData = categoriesWithCountsResponse.data;
            console.log('✅ Got categories with counts:', categoriesData.length);
          } else {
            throw new Error('Invalid response from categories with counts API');
          }
        } catch (error) {
          console.log('❌ Categories with counts failed, trying fallback...', error);
          // Fallback: get all categories hierarchically
          try {
            const fallbackResponse = await categoriesApi.getAll(true); // include_all=true
            if (fallbackResponse?.data && Array.isArray(fallbackResponse.data)) {
              categoriesData = fallbackResponse.data.map((cat: any) => ({
                ...cat,
                count: 0 // Default count for fallback
              }));
              console.log('✅ Got fallback categories:', categoriesData.length);
            } else {
              throw new Error('Invalid response from fallback categories API');
            }
          } catch (fallbackError) {
            console.error('❌ Fallback also failed:', fallbackError);
            categoriesData = []; // Empty as last resort
          }
        }

        // Set categories and expand parents with children
        setCategories(categoriesData);

        if (categoriesData.length > 0) {
          const parentCategoriesWithChildren = categoriesData
            .filter((cat: any) => !cat.parent_id) // Only parent categories
            .filter((parentCat: any) => categoriesData.some((subCat: any) => subCat.parent_id === parentCat.id)) // That have children
            .map((parentCat: any) => parentCat.id);

          setExpandedCategories(parentCategoriesWithChildren);
          console.log('📂 Expanded parent categories:', parentCategoriesWithChildren);
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing sidebar:', error);
        setIsInitialized(true);
      }
    };

    initializeSidebar();
  }, [dispatch]);

  // Sync local state with Redux state - detect URL-based changes and force sync
  useEffect(() => {
    if (isInitialized) {
      // Check if Redux state changed
      const reduxStateChanged = 
        JSON.stringify(category_ids) !== JSON.stringify(localCategoryIds) ||
        JSON.stringify(product_types) !== JSON.stringify(localProductTypes) ||
        JSON.stringify(selectedBrands) !== JSON.stringify(localBrands) ||
        JSON.stringify(selectedConditions) !== JSON.stringify(localConditions) ||
        JSON.stringify(selectedLocations) !== JSON.stringify(localLocations);
      
      if (reduxStateChanged) {
        // Check if this looks like a URL-based change (filters cleared or single filter applied)
        const hadMultipleFilters = (localCategoryIds.length > 1 || localProductTypes.length > 1 || 
                                   localBrands.length > 1 || localConditions.length > 1 || localLocations.length > 1);
        const hasSingleFilter = (category_ids.length === 1 && localCategoryIds.length !== 1) ||
                                (product_types.length === 1 && localProductTypes.length !== 1) ||
                                (category_ids.length === 0 && product_types.length === 0 && 
                                 localCategoryIds.length > 0 || localProductTypes.length > 0);
        
        // If filters were cleared (had filters -> no filters) or single filter applied, it's likely from URL
        if (hasSingleFilter || (hadMultipleFilters && category_ids.length === 0 && product_types.length === 0)) {
          console.log('Detected URL-based filter change, resetting manual update flag and syncing');
          hasManuallyUpdated.current = false;
        }
        
        // Always sync if manual update flag is false
        if (!hasManuallyUpdated.current) {
          console.log('Syncing local state from Redux state');
          console.log('Redux state:', { category_ids, product_types, selectedBrands, selectedConditions, selectedLocations });
          setLocalCategoryIds(category_ids);
          setLocalProductTypes(product_types);
          setLocalBrands(selectedBrands);
          setLocalConditions(selectedConditions);
          setLocalLocations(selectedLocations);
        }
      }
    }
  }, [isInitialized, category_ids, product_types, selectedBrands, selectedConditions, selectedLocations]);

  // Apply filters when any filter changes (but not on initial load)
  useEffect(() => {
    console.log('Filter effect triggered:', { isInitialized, localCategoryIds, localProductTypes, localBrands, localConditions, localLocations, min_price, max_price, search_query });
    
    // Only apply filters if we have manually updated the local state
    if (isInitialized && !isApplyingFilters.current && hasManuallyUpdated.current) {
      // Only apply filters if there are active filters, otherwise let the page load all products
      const hasActiveFilters = localCategoryIds.length > 0 || 
                              localProductTypes.length > 0 || 
                              localBrands.length > 0 || 
                              localConditions.length > 0 || 
                              localLocations.length > 0 || 
                              min_price !== undefined || 
                              max_price !== undefined || 
                              search_query.trim() !== '';
      
      console.log('Has active filters:', hasActiveFilters);
      
      if (hasActiveFilters) {
        console.log('Applying filters:', { localCategoryIds, localProductTypes, localBrands, localConditions, localLocations, min_price, max_price, search_query });
        
        isApplyingFilters.current = true;
        
        // Use a simple timeout instead of useCallback to avoid dependency issues
        const timeoutId = setTimeout(() => {
          const now = Date.now();
          const timeSinceLastCall = now - lastApiCallTime.current;
          
          // Prevent API calls if we've called it recently (within 500ms)
          if (timeSinceLastCall < 500) {
            console.log('Skipping API call - too soon since last call:', timeSinceLastCall, 'ms');
            isApplyingFilters.current = false;
            return;
          }
          
          console.log('Debounced filter executing...');
          lastApiCallTime.current = now;
          dispatch(filterProducts() as any).finally(() => {
            isApplyingFilters.current = false;
            // Reset the manual update flag after API call completes to allow future interactions
            setTimeout(() => {
              hasManuallyUpdated.current = false;
              console.log('Reset manual update flag after API call');
            }, 500);
          });
        }, 300);
        
        return () => {
          clearTimeout(timeoutId);
          isApplyingFilters.current = false;
        };
      } else {
        console.log('No active filters, not applying filter API call');
      }
    } else if (!isInitialized) {
      console.log('Sidebar not initialized yet, skipping filter application');
    } else if (isApplyingFilters.current) {
      console.log('Already applying filters, skipping to prevent infinite loop');
    } else if (!hasManuallyUpdated.current) {
      console.log('No manual updates yet, skipping filter application');
    }
  }, [localCategoryIds, localProductTypes, localBrands, localConditions, localLocations, min_price, max_price, search_query, isInitialized, dispatch]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const handleCategoryToggle = (categoryId: number) => {
    console.log('Category toggle clicked:', categoryId, 'isInitialized:', isInitialized, 'isApplyingFilters:', isApplyingFilters.current);
    console.log('Current localCategoryIds before toggle:', localCategoryIds);
    
    if (!isInitialized) {
      console.log('Sidebar not initialized, skipping toggle');
      return;
    }
    
    if (isApplyingFilters.current) {
      console.log('Already applying filters, skipping toggle to prevent conflicts');
      return;
    }
    
    // Update local state immediately to prevent blinking
    const newLocalCategories = localCategoryIds.includes(categoryId)
      ? localCategoryIds.filter(id => id !== categoryId)
      : [...localCategoryIds, categoryId];
    
    console.log('New localCategoryIds after toggle:', newLocalCategories);
    setLocalCategoryIds(newLocalCategories);
    hasManuallyUpdated.current = true;
    
    console.log('Dispatching toggleCategory for:', categoryId);
    dispatch(toggleCategory(categoryId));
    
    if (onFilterChange) {
      console.log('Calling onFilterChange with new categories:', newLocalCategories);
      onFilterChange('category_ids', newLocalCategories);
    }
  };

  const handleProductTypeToggle = (productType: string) => {
    if (!isInitialized || isApplyingFilters.current) return;
    
    // Update local state immediately
    const newLocalProductTypes = localProductTypes.includes(productType)
      ? localProductTypes.filter(type => type !== productType)
      : [...localProductTypes, productType];
    setLocalProductTypes(newLocalProductTypes);
    hasManuallyUpdated.current = true;

    dispatch(toggleProductType(productType));
    
    if (onFilterChange) {
      onFilterChange('product_types', newLocalProductTypes);
    }
  };

  const handleBrandToggle = (brand: string) => {
    if (!isInitialized || isApplyingFilters.current) return;
    
    // Update local state immediately
    const newLocalBrands = localBrands.includes(brand)
      ? localBrands.filter(b => b !== brand)
      : [...localBrands, brand];
    setLocalBrands(newLocalBrands);
    hasManuallyUpdated.current = true;

    dispatch(toggleBrand(brand));
    
    if (onFilterChange) {
      onFilterChange('brands', newLocalBrands);
    }
  };

  const handleConditionToggle = (condition: string) => {
    if (!isInitialized || isApplyingFilters.current) return;
    
    // Update local state immediately
    const newLocalConditions = localConditions.includes(condition)
      ? localConditions.filter(c => c !== condition)
      : [...localConditions, condition];
    setLocalConditions(newLocalConditions);
    hasManuallyUpdated.current = true;

    dispatch(toggleCondition(condition));
    
    if (onFilterChange) {
      onFilterChange('conditions', newLocalConditions);
    }
  };


  const handlePriceRangeChange = (field: 'min' | 'max', value: number | undefined) => {
    if (!isInitialized || isApplyingFilters.current) return;
    
    hasManuallyUpdated.current = true;
    
    const newPriceRange = {
      min_price: field === 'min' ? value : min_price,
      max_price: field === 'max' ? value : max_price,
    };
    
    dispatch(setPriceRange(newPriceRange));
    
    if (onFilterChange) {
      onFilterChange('price_range', newPriceRange);
    }
  };


  const handleClearAllFilters = () => {
    console.log('Clearing all filters');
    dispatch(clearAllFilters());
    setLocalCategoryIds([]);
    setLocalProductTypes([]);
    setLocalBrands([]);
    setLocalConditions([]);
    setLocalLocations([]);
    hasManuallyUpdated.current = false; // Reset the manual update flag
    if (onFilterChange) {
      onFilterChange('clear_all', true);
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (category_ids.length > 0) count++;
    if (product_types.length > 0) count++;
    if (selectedBrands.length > 0) count++;
    if (selectedConditions.length > 0) count++;
    if (selectedLocations.length > 0) count++;
    if (min_price !== undefined || max_price !== undefined) count++;
    if (search_query.trim()) count++;
    return count;
  };


  return (
    <div className="w-full lg:w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg mr-3">
              <FunnelIcon className="h-5 w-5 text-primary-600" />
            </div>
            Product Filters
            {!isInitialized && (
              <span className="ml-2 text-sm text-gray-500">(Loading...)</span>
            )}
            {isFiltering && (
              <span className="ml-2 text-sm text-blue-500 animate-pulse">(Filtering...)</span>
            )}
          </h2>
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline transition-colors"
              disabled={!isInitialized}
            >
              Clear All
            </button>
          )}
        </div>

        {/* Active Filters Count */}
        {getActiveFiltersCount() > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-blue-700">
                {getActiveFiltersCount()} filter{getActiveFiltersCount() !== 1 ? 's' : ''} active
              </p>
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear all
              </button>
            </div>
            {/* Show active filter tags */}
            <div className="flex flex-wrap gap-2 mt-2">
              {localCategoryIds.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {localCategoryIds.length} categor{localCategoryIds.length === 1 ? 'y' : 'ies'}
                </span>
              )}
              {localProductTypes.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {localProductTypes.length} type{localProductTypes.length === 1 ? '' : 's'}
                </span>
              )}
              {localBrands.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {localBrands.length} brand{localBrands.length === 1 ? '' : 's'}
                </span>
              )}
              {localConditions.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {localConditions.length} condition{localConditions.length === 1 ? '' : 's'}
                </span>
              )}
              {localLocations.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {localLocations.length} location{localLocations.length === 1 ? '' : 's'}
                </span>
              )}
              {(min_price !== undefined && min_price > 0) || (max_price !== undefined && max_price < 100000) ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Price range
                </span>
              ) : null}
              {search_query.trim() && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  "{search_query}"
                </span>
              )}
            </div>
          </div>
        )}

        {/* Search Input - Removed, now in header */}

        {/* Product Types - Removed, now in header */}

        {/* Brands */}
        <div className="mb-6">
          <button
            onClick={() => toggleExpanded('brands')}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            disabled={!isInitialized}
          >
            <div className="flex items-center">
              <TagIcon className="h-4 w-4 mr-2" />
              Brands
              {selectedBrands.length > 0 && (
                <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                  {selectedBrands.length}
                </span>
              )}
            </div>
            {expandedItems.includes('brands') ? (
              <XMarkIcon className="h-4 w-4" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
          </button>
          {expandedItems.includes('brands') && (
            <div className="space-y-2 pl-6 mt-2">
              {/* Select All Option */}
              <label className="flex items-center font-medium text-primary-600">
                <input
                  type="checkbox"
                  checked={localBrands.length === brands.length && brands.length > 0}
                  onChange={() => {
                    if (!isInitialized || isApplyingFilters.current) return;

                    if (localBrands.length === brands.length) {
                      setLocalBrands([]);
                      hasManuallyUpdated.current = true;
                      dispatch(setFilters({ brands: [] }));
                    } else {
                      setLocalBrands(brands.map(b => b.name));
                      hasManuallyUpdated.current = true;
                      dispatch(setFilters({ brands: brands.map(b => b.name) }));
                    }
                  }}
                  disabled={!isInitialized}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <span className="ml-2 text-sm">Select All</span>
              </label>
              {isFilterOptionsLoading ? (
                <div className="text-sm text-gray-500">Loading brands...</div>
              ) : (
                brands.map((brand) => (
                  <label key={brand.name} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localBrands.includes(brand.name)}
                      onChange={() => handleBrandToggle(brand.name)}
                      disabled={!isInitialized}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-600 flex-1">{brand.name}</span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {brand.count}
                    </span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {/* Condition */}
        <div className="mb-6">
          <button
            onClick={() => toggleExpanded('conditions')}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            disabled={!isInitialized}
          >
            <div className="flex items-center">
              <CheckIcon className="h-4 w-4 mr-2" />
              Condition
              {localConditions.length > 0 && (
                <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                  {localConditions.length}
                </span>
              )}
            </div>
            {expandedItems.includes('conditions') ? (
              <XMarkIcon className="h-4 w-4" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
          </button>
          {expandedItems.includes('conditions') && (
            <div className="space-y-2 pl-6 mt-2">
              {/* Select All Option */}
              <label className="flex items-center font-medium text-primary-600">
                <input
                  type="checkbox"
                  checked={localConditions.length === conditions.length && conditions.length > 0}
                  onChange={() => {
                    if (!isInitialized || isApplyingFilters.current) return;

                    if (localConditions.length === conditions.length) {
                      setLocalConditions([]);
                      hasManuallyUpdated.current = true;
                      dispatch(setFilters({ conditions: [] }));
                    } else {
                      setLocalConditions(conditions.map(c => c.name));
                      hasManuallyUpdated.current = true;
                      dispatch(setFilters({ conditions: conditions.map(c => c.name) }));
                    }
                  }}
                  disabled={!isInitialized}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <span className="ml-2 text-sm">Select All</span>
              </label>
              {conditions.map((condition) => (
                <label key={condition.name} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={localConditions.includes(condition.name)}
                    onChange={() => handleConditionToggle(condition.name)}
                    disabled={!isInitialized}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-600 flex-1">{condition.name}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {condition.count}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>


        {/* Price Range */}
        <div className="mb-6">
          <button
            onClick={() => toggleExpanded('priceRange')}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            disabled={!isInitialized}
          >
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-4 w-4 mr-2" />
              Price Range
              {(min_price !== undefined && min_price > 0) || (max_price !== undefined && max_price < dynamicPriceRange.max) ? (
                <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              ) : null}
            </div>
            {expandedItems.includes('priceRange') ? (
              <XMarkIcon className="h-4 w-4" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
          </button>
          {expandedItems.includes('priceRange') && (
            <div className="mt-2 pl-6">
              <div className="space-y-4">
                {/* Price Display - Amazon Style */}
                <div className="text-sm text-gray-900">
                  {min_price !== undefined && min_price > 0 
                    ? `$${min_price.toLocaleString()}` 
                    : '$5'} – {max_price !== undefined && max_price < (dynamicPriceRange?.max || 100000)
                    ? `$${max_price.toLocaleString()}`
                    : '$100+'}
                </div>

                {/* Single Range Slider - Amazon Style (Simplified) */}
                <div className="relative py-2">
                  <input
                    type="range"
                    min={dynamicPriceRange?.min || 0}
                    max={dynamicPriceRange?.max || 100000}
                    step="100"
                    value={max_price !== undefined ? max_price : (dynamicPriceRange?.max || 100000)}
                    onChange={(e) => {
                      const newMax = Number(e.target.value);
                      handlePriceRangeChange('max', newMax);
                    }}
                    disabled={!isInitialized}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((max_price || (dynamicPriceRange?.max || 100000)) / (dynamicPriceRange?.max || 100000)) * 100}%, #e5e7eb ${((max_price || (dynamicPriceRange?.max || 100000)) / (dynamicPriceRange?.max || 100000)) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                </div>

                {/* Predefined Price Ranges - Amazon Style */}
                <div className="space-y-1 pt-2 border-t border-gray-200">
                  <button
                    onClick={() => {
                      handlePriceRangeChange('min', 0);
                      handlePriceRangeChange('max', 25);
                    }}
                    className="w-full text-left text-sm text-gray-700 hover:text-primary-600 py-1"
                  >
                    Up to $25
                  </button>
                  <button
                    onClick={() => {
                      handlePriceRangeChange('min', 25);
                      handlePriceRangeChange('max', 50);
                    }}
                    className="w-full text-left text-sm text-gray-700 hover:text-primary-600 py-1"
                  >
                    $25 to $50
                  </button>
                  <button
                    onClick={() => {
                      handlePriceRangeChange('min', 50);
                      handlePriceRangeChange('max', 100);
                    }}
                    className="w-full text-left text-sm text-gray-700 hover:text-primary-600 py-1"
                  >
                    $50 to $100
                  </button>
                  <button
                    onClick={() => {
                      handlePriceRangeChange('min', 100);
                      handlePriceRangeChange('max', dynamicPriceRange?.max || 100000);
                    }}
                    className="w-full text-left text-sm text-gray-700 hover:text-primary-600 py-1"
                  >
                    $100+
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSidebar;